/**
 * Hotel Checkout Thunk
 *
 * Initiates hotel payment checkout via HOTEL_REVIEW_MCP API with action_type="Payment-Checkout".
 * POST /hotels/checkout
 *
 * Called when user clicks "PAY NOW" button on hotel review page.
 * Returns checkout URL to redirect user to payment page.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchHotelCheckout as fetchHotelCheckoutApi,
  HotelCheckoutError,
  type HotelCheckoutRequestParams,
  type HotelCheckoutData,
} from '../../api/checkout';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface CheckoutResult {
  checkoutData: HotelCheckoutData | null;
  checkoutUrl: string | null;
}

// Re-export types for convenience
export type { HotelCheckoutRequestParams };

// ============================================
// Thunk
// ============================================

/**
 * Initiate hotel checkout
 * POST /hotels/checkout
 *
 * Requires:
 * - transactionKey: from booking review
 * - travellerDetails: array of traveller info
 * - hotelName: hotel name for location_context
 * - hotelId: hotel ID for location_context
 */
export const hotelCheckoutThunk = createAsyncThunk<
  CheckoutResult,
  Omit<HotelCheckoutRequestParams, 'sessionId' | 'userId'>,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/checkout',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const response = await fetchHotelCheckoutApi({
        ...params,
        sessionId,
        userId: getUUID(),
      });

      // Handle API error response
      if (
        response.status === 'RESPONSE_STATUS_ERROR' ||
        (response.errors && response.errors.length > 0)
      ) {
        const errorMessage = response.errors?.[0]?.error_message ?? 'Checkout failed';
        logger.error('[CheckoutThunk] Checkout failed:', response.errors);
        return rejectWithValue(errorMessage);
      }

      // Extract checkout data
      const checkoutData = response.data?.resources?.[0]?.data ?? null;
      const checkoutUrl = checkoutData?.checkoutUrl ?? null;

      if (!checkoutUrl) {
        logger.error('[CheckoutThunk] No checkout URL in response');
        return rejectWithValue('No checkout URL received');
      }

      return {
        checkoutData,
        checkoutUrl,
      };
    } catch (error) {
      logger.error('[CheckoutThunk] Failed to initiate checkout:', error);
      if (error instanceof HotelCheckoutError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to initiate checkout');
    }
  },
  {
    // Prevent duplicate API calls
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isLoading } = state.hotels.checkout;
      return !isLoading;
    },
  },
);
